// The batch list of a rapid-association question's stacked view, ported from
// rapid_association/batches.coffee.
//
// A word is a batch here, and its answer variants are synthesized rather than asked: every word was
// answered one of three ways -- the affirmative, its opposite, or not in time -- so the three are
// built from the labels the question's button set names them by. That makes each word the same
// shape a matrix batch is, which is what lets the matrix batch list draw it.

import { btnsTitle } from '@aytm/app/stats/page/question-views/ra/lib/utils'

const TIMED_OUT = 'timedout'

// The three ways a word could have been answered, named the way this question names them.
const calcBatchAnswerVariants = btnsType => {
  const title = btnsTitle(btnsType)

  return [
    { position: 1, text: title.y },
    { position: 2, text: title.n },
    { position: 3, text: btnsTitle(TIMED_OUT).y }
  ]
}

// Every word, as a batch of the three answers. A word in a list of them is always shown -- the list
// is what the researcher picks from; a lone word keeps whatever the question said about it, since
// there is no list to pick it out of.
export const calcBatches = ({ answerVariants, btnsType } = {}) => {
  const words = (answerVariants || []).filter(Boolean)
  const batchAnswerVariants = calcBatchAnswerVariants(btnsType)

  return words.map(word => ({
    ...word,
    hidden: 1 < words.length ? false : word.hidden,
    answerVariants: batchAnswerVariants.map(variant => ({ ...variant }))
  }))
}

// What one word was answered, in the shape a matrix batch reports: the word's own totals, and the
// three answers beneath it.
export const calcBatchAnswer = ({ answer, position } = {}) => {
  if ('loading' === answer) return 'loading'

  const answered = answer?.[`a${position}`]
  if (!answered) return { total: 0 }

  return {
    amount: answered.amount,
    percents: answered.percents,
    total: answer?.total || 0,
    respTotal: answer?.total || 0,
    a1: answered.y || {},
    a2: answered.n || {},
    a3: answered.na || {}
  }
}

// Everything one word draws from: what it asks, what was answered, and the params it owns. The
// word is a question of its own as far as the report params go, addressed by the key and id the
// retired sub-item view derived.
export const buildBatchProps =
  ({
    questionId,
    questionKey,
    type,
    format,
    components,
    answer,
    defaultSortOrder,
    batchParams = {},
    handlers = {}
  } = {}) =>
  batch => {
    const position = batch.position

    const id = `${questionId}-${position}`
    const key = `${questionKey}.${position}`

    return {
      id,
      questionKey: key,
      // a word carries the question's own type, which is what tells the batch it could cloud
      questionType: type,
      item: batch,
      answer: calcBatchAnswer({ answer, position }),
      answerVariants: batch.answerVariants,
      format: { ...(format?.subitems || format), ignoreRefs: true },
      components: { ...(components?.subitems || components) },
      defaultSortOrder,
      params: batchParams[position] || {},
      onReady: (api, meta) => handlers.onBatchReady?.(position, api, { ...meta, uid: batch.uid ?? position }),
      // the position is what the api registry is keyed by, the key what the params hang off
      onStateChange: reason => handlers.onBatchStateChange?.(position, key, reason),
      onWcParamsChange: wcParams => handlers.onBatchWcParamsChange?.(id, wcParams)
    }
  }
